import React, {type ReactNode} from 'react';
import {Checkbox as BaseCheckbox} from 'reakit/Checkbox';
import {CheckboxButton} from './CheckboxButton';
import {useCheckbox} from './useCheckbox';
import {
  CheckboxWrapperView,
  CheckboxInputWrapperView,
  CheckboxInputView,
} from './Checkbox.styles';
import {FormLabel} from '../FormLabel';
import {Text} from '../Text';
import type {
  HTMLAttributes,
  SizeContextSize,
  SpringTooling,
} from '../__SharedTypes';
import {mergeRefs, ZERO_WIDTH_SPACE_CHARACTER} from '../../utils';

export type CheckboxOnChangeMetaData = {
  nextIsChecked: boolean;
};

export type CheckboxProps = Omit<HTMLAttributes, 'onChange'> &
  SpringTooling & {
    /**
     * The color of the checkbox in its non-error state.
     */
    colorVariant?: 'blue' | 'purple' | 'green';
    /**
     * id used for Jest and Cypress test identification.
     */
    ['data-automation-id']?: string;
    /**
     * For uncontrolled Checkboxes, sets the initial state to checked.
     */
    defaultChecked?: boolean;
    /**
     * Determines if the checkbox should render as checked or unchecked. Setting `isChecked` makes the `Checkbox`
     * instance controlled, and should be used with `onChange`.
     */
    isChecked?: boolean;
    /**
     * When `isDisabled` is passed to `Checkbox`, it will render as unclickable and the label will be visually
     * de-emphasized. An `aria-disabled` value of true will also be added to the input.
     */
    isDisabled?: boolean;
    /**
     * Determines if the Checkbox should render with error styling. `isError` will change the background color
     * of the checked and indeterminate states and the border color of the unchecked state.
     */
    isError?: boolean;
    /**
     * Determines if the Checkbox should render in an indeterminate state. This takes precedence over isChecked.
     */
    isIndeterminate?: boolean;
    /**
     * Indicates whether or not the field is required and is used to make Checkbox more accessible to assistive technologies.
     */
    isRequired?: boolean;
    /**
     * Setting `label` creates a label element and automatically gives it a `for` value that matches the `id` of the HTML input.
     */
    label?: ReactNode;
    /**
     * Sets the `name` atribute of the checkbox. This is primarily used for multiple checkboxes whose values should be
     * grouped (e.g. the answers for a single multiple choice question). The `name` attribute is also used to identify
     * the input when referencing submitted form data.
     */
    name?: string;
    /**
     * A custom size setting which overrides the theme default
     */
    size?: SizeContextSize;
    /**
     * The value for the Checkbox. This is primarily used for multiple checkboxes that share the same state or `name`.
     * If you are using a single checkbox, you will generally not need to specify a `value`.
     */
    value?: string;
    /**
     * A callback for when the checked state changes in an uncontrolled Checkbox,
     * or a controller function for when a controlled Checkbox is clicked.
     */
    onChange?: (
      event: React.ChangeEvent<HTMLInputElement>,
      metadata: CheckboxOnChangeMetaData
    ) => void;
  };

/**
 * # Checkbox
 *
 * Checkbox is a self-closing form component that renders an accesible dual or tri-state button.
 *
 * ## Usage
 *
 * ```jsx
 * import {Checkbox} from '@packages/systems/spring';
 * const Example = () => {
 *   const [checked, setChecked] = React.useState(false);
 *   const toggle = () => setChecked(!checked);
 *
 *   return <Checkbox isChecked={checked} onChange={toggle} label='I agree to the terms' />;
 * };
 * ```
 *
 * @component
 */
export const Checkbox = React.forwardRef<HTMLElement, CheckboxProps>(
  (
    {
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      colorVariant = 'blue',
      className,
      defaultChecked,
      id: idProp,
      isChecked: isCheckedProp,
      isDisabled: isDisabledProp,
      isError: isErrorProp,
      isIndeterminate = false,
      isRequired: isRequiredProp,
      label,
      name: nameProp,
      value,
      size: sizeProp,
      onChange: onChangeProp,
      ...props
    }: CheckboxProps,
    ref
  ) => {
    const {
      ariaDescribedBy,
      checked,
      id,
      name,
      onChange,
      isDisabled,
      isError,
      isRequired,
      shouldHideRequired,
      size,
      reakitCheckboxProps,
    } = useCheckbox({
      isChecked: isCheckedProp,
      defaultChecked,
      isDisabled: isDisabledProp,
      isError: isErrorProp,
      isIndeterminate,
      isRequired: isRequiredProp,
      id: idProp,
      name: nameProp,
      onChange: onChangeProp,
      size: sizeProp,
      value,
    });

    return (
      <BaseCheckbox
        aria-describedby={ariaDescribedBy}
        aria-disabled={isDisabled}
        checked={checked}
        id={id}
        onChange={onChange}
        {...reakitCheckboxProps}
        {...props}
      >
        {/* @ts-expect-error [PDTY-2706] - TS2769 - No overload matches this call. */}
        {(checkboxProps) => {
          const mergedRefs = mergeRefs([checkboxProps.ref, ref]);

          return (
            <CheckboxWrapperView
              align="start"
              className={className}
              isDisabled={Boolean(isDisabled)}
              isFullWidth={false}
              // shouldBypassAria: We are using the isDisabled prop for opacity styles, but we don't
              // actually want to add the `aria-disabled` attribute to this <div> in the HTML.
              shouldBypassAria
              size={size}
            >
              <CheckboxInputWrapperView>
                <CheckboxInputView
                  {...checkboxProps}
                  aria-label={ariaLabel}
                  aria-labelledby={ariaLabelledBy}
                  // add form="" to disabled checkboxes to prevent form submissions from including them
                  form={isDisabled ? '' : undefined}
                  name={name}
                  ref={mergedRefs}
                  required={isRequired}
                  size={size}
                  value={value}
                />
                <CheckboxButton
                  colorVariant={colorVariant}
                  // @ts-expect-error - TS2339 - Property 'checked' does not exist on type 'ExtractHTMLAttributes<any>'.
                  isChecked={checkboxProps.checked}
                  isDisabled={isDisabled}
                  isError={isError}
                  isIndeterminate={isIndeterminate}
                  // shouldBypassAria: We are using the isChecked, isDisabled, and isIndeterminate props for styles,
                  // but we don't actually want to add `aria-checked`, `aria-disabled`, or `data-indeterminate` attributes
                  // to this <div> component. The CheckboxInputView component provides an actual <input type="checkbox" />
                  // element with all the necessary semantic markup in the HTML.
                  shouldBypassAria
                  size={size}
                />
                <TextAlignmentSpacer size={size} />
              </CheckboxInputWrapperView>
              {label ? (
                <FormLabel
                  _shouldHideRequired={shouldHideRequired}
                  htmlFor={id}
                  isRequired={isRequired}
                >
                  {label}
                </FormLabel>
              ) : null}
            </CheckboxWrapperView>
          );
        }}
      </BaseCheckbox>
    );
  }
);

Checkbox.displayName = 'Checkbox';
Checkbox.toString = CheckboxWrapperView.toString;

// @ts-expect-error - TS2552 - Cannot find name 'SizeContextSizeValue'. Did you mean 'SizeContextSize'?
const TextAlignmentSpacer = (props: {size: SizeContextSizeValue}) => (
  <Text {...props}>{ZERO_WIDTH_SPACE_CHARACTER}</Text>
);

export default Checkbox;
